import { useEffect, useState, useRef } from 'react';
import './styles.scss';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateHeader } from '../../slices/header-slice';
import { formatDate } from '../../utility/utils';
import SEO from '../../components/seo';
import { getMailChimpCampaigns } from '../../api';
import { useDataLoading } from '../../hooks/useDataLoading';

const Media = ({ media }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [combinedContent, setCombinedContent] = useState([]);
    const hasFetched = useRef(false);

    // Register loading state with global spinner
    useDataLoading('media', isDataLoading, []);

    useEffect(() => {
        dispatch(updateHeader('light'));
    }, [dispatch]);

    useEffect(() => {
        const fetchAndCombineContent = async () => {
            // Check if we already have combined content or if we're already fetching
            if (combinedContent.length > 0 || hasFetched.current) return;

            try {
                hasFetched.current = true;
                setIsDataLoading(true);

                // Fetch MailChimp campaigns
                const campaignsResponse = await getMailChimpCampaigns();
                const campaigns = campaignsResponse.data || [];

                // Transform campaigns to match media post structure
                const transformedCampaigns = campaigns.map(campaign => ({
                    id: `campaign-${campaign.campaignId}`,
                    title: campaign.title,
                    content: `<p>${campaign.description}</p>`,
                    visibility: "Published",
                    images: campaign.imageUrl ? [{
                        id: `img-${campaign.campaignId}`,
                        mediaPostId: campaign.campaignId,
                        fileName: campaign.imageUrl
                    }] : [],
                    videos: [],
                    date: campaign.createdDate,
                    metaDataJson: "{}",
                    imageFiles: [],
                    embeddedVideoUrls: [],
                    metaData: { seoData: {} },
                    type: 'campaign'
                }));

                // Transform media posts to include type
                const transformedMedia = (media || []).map(post => ({
                    ...post,
                    type: 'media'
                }));

                // Combine and sort by date
                const allContent = [...transformedMedia, ...transformedCampaigns];
                const sortedContent = allContent.sort((a, b) => new Date(b.date) - new Date(a.date));

                setCombinedContent(sortedContent);
            } catch (error) {
                console.error('Error fetching campaigns:', error);
                // Fallback to just media posts if campaigns fail
                const transformedMedia = (media || []).map(post => ({
                    ...post,
                    type: 'media'
                }));
                setCombinedContent(transformedMedia);
            } finally {
                setIsDataLoading(false);
            }
        };

        fetchAndCombineContent();
    }, [media, combinedContent.length]);

    const onMediaPostClick = (post) => {
        if (!post?.id) return; // Safety check
        
        if (post.type === 'campaign') {
            // Handle campaign click - could open in new tab or show modal
            navigate(`/newsletter/${post.id.replace('campaign-', '')}`);
        } else {
            navigate(`/media/${post.id}`);
        }
    };

    const renderImage = (post) => {
        if (!post) return null; // Safety check
        
        if (post.type === 'campaign' && post.images?.[0]?.fileName) {
            // For campaigns, use the direct image URL
            return <img alt={post.title || 'Campaign image'} src={post.images[0].fileName} />;
        } else if (post.images?.[0]?.fileName) {
            // For media posts, use the optimized image API
            return <img alt={post.title || 'Media post image'} src={`${process.env.REACT_APP_WEBSITE_API}/api/images/optimize?filePath=images/media/${post.images[0].fileName}&width=350&height=230&quality=99&format=webp`} />;
        }
        return null;
    };


    return (
        <div className="media-page">
            <SEO
                title="Paramount Wood Co."
                description="A collection of historic news and insights from Paramount Woof Co. descending in chronological order.If you wish to receive these articles in real time, please subscribe to our newsletter for regular updates and happenings from the studio."
                name="Paramount Wood Co."
                type="website"
                url="https://paramountwoodco.com/media"
            />

            <div className="media-intro">
                <h1>Media</h1>
                <p>A collection of historic news and insights from Paramount Woof Co. descending in chronological order.If you wish to receive these articles in real time, please subscribe to our newsletter for regular updates and happenings from the studio.</p>
            </div>

            <div className='content'>
                <div className='media-content'>
                    {combinedContent && combinedContent.length > 0 ? (
                        combinedContent.map((post, i) => (
                            <div key={post?.id || i} className={`media-post ${post?.type === 'campaign' ? 'campaign-post' : ''}`}>
                                <div className='post-info'>
                                    <div>
                                        {post?.type === 'campaign' && (
                                            <span className='campaign-badge'>Newsletter</span>
                                        )}
                                        <span className='date'>{formatDate(post?.date)}</span>
                                        <h2>{post?.title || 'Untitled'}</h2>
                                        <div className='html-text' dangerouslySetInnerHTML={{ __html: post?.content || '' }} />

                                    </div>
                                    <button
                                        className={`cta gold-fill`}
                                        onClick={() => onMediaPostClick(post)}
                                    >
                                        {post?.type === 'campaign' ? 'Read More' : 'View More'}
                                    </button>
                                </div>

                                <div className='post-image'>
                                    {renderImage(post)}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="no-content">
                            <p>No media content available at the moment.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Media;
